import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const RECETTES = {
  vlc: { nom: "Vodka Lemon Cassis", ingredients: { limonade: 0.7, vodka: 0.2, sirop_cassis: 0.1 } },
  vlp: { nom: "Vodka Lemon Pêche", ingredients: { limonade: 0.7, vodka: 0.2, sirop_peche: 0.1 } },
  sotb: { nom: "Sex on the Beach", ingredients: { ananas: 0.24, cramberry: 0.24, orange: 0.24, sirop_peche: 0.12, vodka: 0.17 } },
  gin_fizz: { nom: "Gin Fizz", ingredients: { limonade: 0.71, pulco: 0.12, gin: 0.17 } },
  maurice: { nom: "Maurice", ingredients: { ananas: 0.7, malibu: 0.1, rhum: 0.1, sirop_grenadine: 0.1 } },
  mdma: { nom: "MDMA Cocktail", ingredients: { tequila: 0.2, sirop_sucre: 0.2, pomme: 0.6 } },
};

const PRIX_INGREDIENTS = {
  ananas: 1.5, cramberry: 2.15, orange: 1, limonade: 0.25, vodka: 14.60,
  sirop_cassis: 3.72, sirop_peche: 2.33, gin: 16, pulco: 3.30, malibu: 12.50,
  rhum: 15, sirop_grenadine: 1.75, tequila: 17, sirop_sucre: 1, pomme: 1.25,
};

// Proportions des cocktails dans le total
const PROPORTIONS_COCKTAILS = {
  vlp: 0.2,
  vlc: 0.2,
  sotb: 0.17,
  gin_fizz: 0.25,
  maurice: 0.18,
  mdma: 0.1,
};

interface CalculResultats {
  totalBoissons: number;
  coutTotal: number;
  detailsCocktails: { [key: string]: number };
  ingredientsNecessaires: { [key: string]: { quantite: number; cout: number } };
}

export const DrinkCalculatorPage = () => {
  const navigate = useNavigate();
  const [budget, setBudget] = useState<number>(1900);
  const [resultats, setResultats] = useState<CalculResultats | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // États pour le calculateur de cocktail individuel
  const cocktailKeys = Object.keys(RECETTES) as Array<keyof typeof RECETTES>;
  const [selectedSingleCocktail, setSelectedSingleCocktail] = useState<keyof typeof RECETTES>(cocktailKeys[0]);
  const [singleCocktailQuantity, setSingleCocktailQuantity] = useState<number>(1);

  const calculerCoutTotalPourQuantite = (totalBoissons: number) => {
    let coutTotalCalcule = 0;
    const ingredientsCalcules: { [key: string]: number } = {};

    for (const [cocktailKey, proportionCocktail] of Object.entries(PROPORTIONS_COCKTAILS)) {
      const quantiteCocktail = proportionCocktail * totalBoissons;
      const recette = RECETTES[cocktailKey as keyof typeof RECETTES].ingredients;
      for (const [ingredient, proportionIngredient] of Object.entries(recette)) {
        ingredientsCalcules[ingredient] = (ingredientsCalcules[ingredient] || 0) + proportionIngredient * quantiteCocktail;
      }
    }

    for (const [ingredient, quantite] of Object.entries(ingredientsCalcules)) {
      coutTotalCalcule += PRIX_INGREDIENTS[ingredient as keyof typeof PRIX_INGREDIENTS] * quantite;
    }
    return { cout: coutTotalCalcule, ingredients: ingredientsCalcules };
  };

  const handleCalculer = () => {
    setLoading(true);
    setResultats(null);

    // Recherche dichotomique pour trouver la quantité maximale
    let debut = 0;
    let fin = 20000; // Augmenter si nécessaire, limite supérieure pour la recherche
    let meilleurTotal = 0;
    let meilleursIngredientsCalcul = {};
    let coutFinal = 0;

    // Simuler une petite attente pour l'effet de chargement
    setTimeout(() => {
      while (fin - debut > 0.1) { // Précision pour le nombre de boissons
        const totalBoissonsTest = (debut + fin) / 2;
        const { cout, ingredients } = calculerCoutTotalPourQuantite(totalBoissonsTest);

        if (cout <= budget) {
          meilleurTotal = totalBoissonsTest;
          meilleursIngredientsCalcul = ingredients;
          coutFinal = cout;
          debut = totalBoissonsTest;
        } else {
          fin = totalBoissonsTest;
        }
      }

      const detailsCocktailsCalcules: { [key: string]: number } = {};
      for (const [cocktailKey, proportion] of Object.entries(PROPORTIONS_COCKTAILS)) {
        detailsCocktailsCalcules[cocktailKey] = proportion * meilleurTotal;
      }

      const ingredientsNecessairesCalcules: { [key: string]: { quantite: number; cout: number } } = {};
      for (const [nom, qte] of Object.entries(meilleursIngredientsCalcul)) {
        const prixUnit = PRIX_INGREDIENTS[nom as keyof typeof PRIX_INGREDIENTS];
        ingredientsNecessairesCalcules[nom] = {
          quantite: qte as number,
          cout: (qte as number) * prixUnit,
        };
      }
      
      setResultats({
        totalBoissons: meilleurTotal,
        coutTotal: coutFinal,
        detailsCocktails: detailsCocktailsCalcules,
        ingredientsNecessaires: ingredientsNecessairesCalcules,
      });
      setLoading(false);
    }, 500);
  };

  // Calcul pour le cocktail individuel
  const singleCocktailIngredients = useMemo(() => {
    if (!selectedSingleCocktail || singleCocktailQuantity <= 0) return null;

    const recette = RECETTES[selectedSingleCocktail].ingredients;
    const ingredientsCalculated: { [key: string]: number } = {};
    for (const [ingredient, proportion] of Object.entries(recette)) {
      ingredientsCalculated[ingredient] = proportion * singleCocktailQuantity;
    }
    return ingredientsCalculated;
  }, [selectedSingleCocktail, singleCocktailQuantity]);

  return (
    <div className="min-h-screen bg-gray-100 p-8 pt-24 md:pt-32">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Calculateur de Production de Boissons</h1>
          <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm md:text-base"
          >
            Retour au tableau de bord
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-xl p-6 md:p-8 mb-8">
          <div className="mb-6">
            <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-1">
              Budget disponible (€)
            </label>
            <input
              type="number"
              id="budget"
              value={budget}
              onChange={(e) => setBudget(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
            />
          </div>
          <button
            onClick={handleCalculer}
            disabled={loading}
            className="w-full px-6 py-3 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 transition-colors disabled:bg-pink-300"
          >
            {loading ? 'Calcul en cours...' : 'Calculer la Production Maximale'}
          </button>
        </div>

        {resultats && (
          <div className="bg-white rounded-xl shadow-xl p-6 md:p-8">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-6 text-center">Résultats de la Simulation</h2>
            
            <div className="bg-pink-50 p-4 rounded-lg mb-6 text-center">
              <p className="text-lg text-pink-700">
                Quantité totale de litres de cocktails possibles : 
                <span className="font-bold text-xl ml-2">{resultats.totalBoissons.toFixed(2)} L</span>
              </p>
              <p className="text-lg text-pink-700">
                Coût total estimé : 
                <span className="font-bold text-xl ml-2">{resultats.coutTotal.toFixed(2)} €</span>
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-3">Détail par cocktail (en litres) :</h3>
                <ul className="space-y-1 text-sm text-gray-600">
                  {Object.entries(resultats.detailsCocktails).map(([cocktailKey, quantiteTotaleCocktail]) => {
                    const recetteCocktail = RECETTES[cocktailKey as keyof typeof RECETTES].ingredients;
                    return (
                      <li key={cocktailKey} className="mb-3 p-2 border rounded-md bg-gray-50">
                        <div className="flex justify-between font-medium uppercase">
                          <span>{RECETTES[cocktailKey as keyof typeof RECETTES].nom}:</span>
                          <span>{quantiteTotaleCocktail.toFixed(2)} L</span>
                        </div>
                        <ul className="pl-4 mt-1 text-xs">
                          {Object.entries(recetteCocktail).map(([ingredient, proportion]) => (
                            <li key={ingredient} className="flex justify-between">
                              <span>{ingredient.charAt(0).toUpperCase() + ingredient.slice(1)}:</span>
                              <span>{(proportion * quantiteTotaleCocktail).toFixed(3)} L</span>
                            </li>
                          ))}
                        </ul>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-3">Ingrédients nécessaires :</h3>
                <ul className="space-y-1 text-sm text-gray-600">
                  {Object.entries(resultats.ingredientsNecessaires).map(([nom, data]) => (
                    <li key={nom} className="flex justify-between">
                      <span>{nom.charAt(0).toUpperCase() + nom.slice(1)}:</span>
                      <span>{data.quantite.toFixed(2)} L (Coût: {data.cout.toFixed(2)}€)</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Section pour le calculateur de cocktail individuel */}
        <div className="bg-white rounded-xl shadow-xl p-6 md:p-8 mt-8">
          <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-6 text-center">Calculateur d'Ingrédients par Cocktail</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4 items-end">
            <div>
              <label htmlFor="singleCocktailSelect" className="block text-sm font-medium text-gray-700 mb-1">
                Choisir un cocktail :
              </label>
              <select
                id="singleCocktailSelect"
                value={selectedSingleCocktail}
                onChange={(e) => setSelectedSingleCocktail(e.target.value as keyof typeof RECETTES)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                {cocktailKeys.map(key => (
                  <option key={key} value={key}>{RECETTES[key].nom}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="singleCocktailQuantity" className="block text-sm font-medium text-gray-700 mb-1">
                Quantité désirée (L) :
              </label>
              <input
                type="number"
                id="singleCocktailQuantity"
                value={singleCocktailQuantity}
                onChange={(e) => setSingleCocktailQuantity(parseFloat(e.target.value) || 0)}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {selectedSingleCocktail && singleCocktailIngredients && (
            <div>
              <h3 className="text-md font-semibold text-gray-700 mt-4 mb-2">
                Ingrédients pour {singleCocktailQuantity.toFixed(2)} L de {RECETTES[selectedSingleCocktail].nom} :
              </h3>
              <ul className="space-y-1 text-sm text-gray-600 bg-indigo-50 p-3 rounded-md">
                {Object.entries(singleCocktailIngredients).map(([nom, quantite]) => (
                  <li key={nom} className="flex justify-between">
                    <span>{nom.charAt(0).toUpperCase() + nom.slice(1)}:</span>
                    <span>{quantite.toFixed(3)} L</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}; 